import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogContent, Box, Typography, TextField, Button, Alert,
  InputAdornment, IconButton, CircularProgress, Tabs, Tab, Divider,
  Stepper, Step, StepLabel,
} from '@mui/material';
import {
  Email, Lock, Person, Visibility, VisibilityOff,
  Key, AutoAwesome, Close, ArrowBack,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { authAPI } from '../../services/api';

const REG_STEPS = ['Account Details', 'Verify Email'];

export default function AuthModal() {
  const { authModal, closeAuthModal, setUser, openAuthModal } = useAuth();
  const navigate = useNavigate();

  // Only render when open WITHOUT a guard message (guard message = different modal)
  const isOpen = authModal.open && !authModal.guardMessage;

  const [tab, setTab] = useState(0);

  // Login state
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [showLoginPw, setShowLoginPw] = useState(false);
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState('');

  // Register state
  const [regStep, setRegStep] = useState(0);
  const [regForm, setRegForm] = useState({ name: '', email: '', password: '', confirmPw: '' });
  const [regOtp, setRegOtp] = useState('');
  const [showRegPw, setShowRegPw] = useState(false);
  const [regLoading, setRegLoading] = useState(false);
  const [regError, setRegError] = useState('');
  const [regSuccess, setRegSuccess] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [resendTimer, setResendTimer] = useState(0);

  // Sync tab from modal state when it opens
  useEffect(() => {
    if (isOpen) setTab(authModal.tab ?? 0);
  }, [isOpen, authModal.tab]);

  // ── Login ─────────────────────────────────────────────────────────────────
  const handleLoginSubmit = async (e) => {
    e.preventDefault();
    setLoginError('');
    setLoginLoading(true);
    try {
      const res = await authAPI.directLogin({ email: loginForm.email, password: loginForm.password });
      const { accessToken, user: userData } = res.data.data;
      localStorage.setItem('token', accessToken);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      closeAuthModal();
      resetForms();
    } catch (err) {
      setLoginError(err.response?.data?.message || 'Invalid email or password');
    } finally {
      setLoginLoading(false);
    }
  };

  // ── Register step 1: send OTP ─────────────────────────────────────────────
  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (regForm.password !== regForm.confirmPw) {
      setRegError('Passwords do not match');
      return;
    }
    setRegError('');
    setRegLoading(true);
    try {
      const res = await authAPI.sendRegisterOtp({
        name: regForm.name,
        email: regForm.email,
        password: regForm.password,
      });
      setMaskedEmail(res.data.data?.email || regForm.email);
      setRegSuccess('Verification code sent to your email!');
      setRegStep(1);
      startResendTimer();
    } catch (err) {
      setRegError(err.response?.data?.message || 'Registration failed. Please try again.');
    } finally {
      setRegLoading(false);
    }
  };

  // ── Register step 2: verify OTP ───────────────────────────────────────────
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    if (regOtp.length !== 6) { setRegError('Enter 6-digit code'); return; }
    setRegError('');
    setRegLoading(true);
    try {
      const res = await authAPI.verifyRegisterOtp({
        name: regForm.name,
        email: regForm.email,
        password: regForm.password,
        otp: regOtp,
      });
      const { accessToken, user: userData } = res.data.data;
      localStorage.setItem('token', accessToken);
      localStorage.setItem('user', JSON.stringify(userData));
      setUser(userData);
      closeAuthModal();
      resetForms();
    } catch (err) {
      setRegError(err.response?.data?.message || 'Invalid or expired code. Try again.');
    } finally {
      setRegLoading(false);
    }
  };

  const handleResend = async () => {
    if (resendTimer > 0) return;
    setRegError(''); setRegSuccess('');
    try {
      await authAPI.sendRegisterOtp({ name: regForm.name, email: regForm.email, password: regForm.password });
      setRegSuccess('New code sent!');
      startResendTimer();
    } catch { setRegError('Failed to resend. Try again.'); }
  };

  const startResendTimer = () => {
    setResendTimer(60);
    const interval = setInterval(() => {
      setResendTimer(t => { if (t <= 1) { clearInterval(interval); return 0; } return t - 1; });
    }, 1000);
  };

  const resetForms = () => {
    setLoginForm({ email: '', password: '' });
    setLoginError('');
    setRegForm({ name: '', email: '', password: '', confirmPw: '' });
    setRegOtp('');
    setRegStep(0);
    setRegError('');
    setRegSuccess('');
    setResendTimer(0);
  };

  const handleClose = () => { closeAuthModal(); resetForms(); };

  const handleTabChange = (_, v) => { setTab(v); resetForms(); };

  const goToForgotPassword = () => {
    closeAuthModal();
    navigate('/forgot-password');
  };

  // Common TextField sx
  const fieldSx = { mb: 2 };

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          background: 'linear-gradient(145deg, rgba(5,13,26,0.98) 0%, rgba(10,22,40,0.98) 100%)',
          backdropFilter: 'blur(30px)',
          border: '1px solid rgba(255,255,255,0.18)',
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      {/* Glow accent at top */}
      <Box sx={{
        height: 3,
        background: 'linear-gradient(90deg, #0288d1, #4fc3f7, #673ab7)',
      }} />

      <DialogContent sx={{ p: { xs: 3, sm: 4 }, pt: 3 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{
              p: 0.8, borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.12)',
              border: '1px solid rgba(255,255,255,0.25)',
              display: 'flex',
            }}>
              <AutoAwesome sx={{ color: 'primary.main', fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" sx={{ fontWeight: 800, color: 'primary.light', lineHeight: 1.1 }}>
                SpacePulse
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: 10 }}>
                Space Weather Monitor
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary', '&:hover': { color: 'text.primary' } }}>
            <Close fontSize="small" />
          </IconButton>
        </Box>

        {/* Tabs */}
        <Tabs
          value={tab}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{
            mb: 3,
            bgcolor: 'rgba(255,255,255,0.03)',
            borderRadius: 2,
            border: '1px solid rgba(255,255,255,0.08)',
            '& .MuiTab-root': { color: 'text.secondary', fontWeight: 600, fontSize: 13, textTransform: 'none' },
            '& .Mui-selected': { color: 'primary.light' },
            '& .MuiTabs-indicator': { bgcolor: 'primary.main', height: 2 },
          }}
        >
          <Tab label="Sign In" />
          <Tab label="Create Account" />
        </Tabs>

        {/* ── LOGIN PANEL ────────────────────────────────────────────────── */}
        {tab === 0 && (
          <form onSubmit={handleLoginSubmit}>
            {loginError && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2, fontSize: 13 }}>{loginError}</Alert>
            )}

            <TextField
              fullWidth label="Email Address" type="email"
              value={loginForm.email}
              onChange={e => setLoginForm(f => ({ ...f, email: e.target.value }))}
              required autoFocus sx={fieldSx}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Email sx={{ color: 'primary.main', fontSize: 18 }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth label="Password"
              type={showLoginPw ? 'text' : 'password'}
              value={loginForm.password}
              onChange={e => setLoginForm(f => ({ ...f, password: e.target.value }))}
              required sx={{ mb: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock sx={{ color: 'primary.main', fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setShowLoginPw(v => !v)}>
                      {showLoginPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ textAlign: 'right', mb: 2.5 }}>
              <Typography
                variant="caption"
                onClick={goToForgotPassword}
                sx={{ color: 'primary.main', cursor: 'pointer', '&:hover': { color: 'primary.light' } }}
              >
                Forgot Password?
              </Typography>
            </Box>

            <Button
              type="submit" fullWidth variant="contained" size="large"
              disabled={loginLoading}
              sx={{ py: 1.4, fontWeight: 700, fontSize: 14, borderRadius: 2 }}
            >
              {loginLoading ? <CircularProgress size={20} color="inherit" /> : 'Sign In to SpacePulse'}
            </Button>
          </form>
        )}

        {/* ── REGISTER PANEL ─────────────────────────────────────────────── */}
        {tab === 1 && (
          <>
            <Stepper activeStep={regStep} sx={{ mb: 3 }}>
              {REG_STEPS.map(label => (
                <Step key={label}>
                  <StepLabel
                    sx={{
                      '& .MuiStepLabel-label': { color: 'text.secondary', fontSize: 11 },
                      '& .MuiStepLabel-label.Mui-active': { color: 'primary.light' },
                      '& .MuiStepIcon-root.Mui-active': { color: 'primary.main' },
                      '& .MuiStepIcon-root.Mui-completed': { color: '#66bb6a' },
                    }}
                  >
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>

            {regError   && <Alert severity="error"   sx={{ mb: 2, borderRadius: 2, fontSize: 13 }}>{regError}</Alert>}
            {regSuccess && <Alert severity="success" sx={{ mb: 2, borderRadius: 2, fontSize: 13 }}>{regSuccess}</Alert>}

            {/* Step 0 — Details */}
            {regStep === 0 && (
              <form onSubmit={handleSendOtp}>
                <TextField
                  fullWidth label="Full Name" value={regForm.name}
                  onChange={e => setRegForm(f => ({ ...f, name: e.target.value }))}
                  required autoFocus sx={fieldSx}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Person sx={{ color: 'primary.main', fontSize: 18 }} /></InputAdornment> }}
                />
                <TextField
                  fullWidth label="Email Address" type="email" value={regForm.email}
                  onChange={e => setRegForm(f => ({ ...f, email: e.target.value }))}
                  required sx={fieldSx}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Email sx={{ color: 'primary.main', fontSize: 18 }} /></InputAdornment> }}
                />
                <TextField
                  fullWidth label="Password"
                  type={showRegPw ? 'text' : 'password'}
                  value={regForm.password}
                  onChange={e => setRegForm(f => ({ ...f, password: e.target.value }))}
                  required sx={fieldSx}
                  helperText="Minimum 6 characters"
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Lock sx={{ color: 'primary.main', fontSize: 18 }} /></InputAdornment>,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton size="small" onClick={() => setShowRegPw(v => !v)}>
                          {showRegPw ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <TextField
                  fullWidth label="Confirm Password" type="password"
                  value={regForm.confirmPw}
                  onChange={e => setRegForm(f => ({ ...f, confirmPw: e.target.value }))}
                  required sx={{ mb: 3 }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Lock sx={{ color: 'primary.main', fontSize: 18 }} /></InputAdornment> }}
                />
                <Button
                  type="submit" fullWidth variant="contained" size="large"
                  disabled={regLoading}
                  sx={{ py: 1.4, fontWeight: 700, fontSize: 14, borderRadius: 2 }}
                >
                  {regLoading ? <CircularProgress size={20} color="inherit" /> : 'Send Verification Code'}
                </Button>
              </form>
            )}

            {/* Step 1 — OTP */}
            {regStep === 1 && (
              <form onSubmit={handleVerifyOtp}>
                <Box sx={{ textAlign: 'center', mb: 3 }}>
                  <Typography sx={{ fontSize: 44 }}>📧</Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5 }}>
                    Verification code sent to
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: 'primary.light', fontWeight: 700 }}>
                    {maskedEmail}
                  </Typography>
                </Box>

                <TextField
                  fullWidth label="6-Digit Code"
                  value={regOtp}
                  onChange={e => setRegOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  required autoFocus sx={{ mb: 3 }}
                  inputProps={{
                    maxLength: 6,
                    style: { textAlign: 'center', fontSize: 26, letterSpacing: 10, fontWeight: 800 },
                  }}
                  InputProps={{
                    startAdornment: <InputAdornment position="start"><Key sx={{ color: 'primary.main' }} /></InputAdornment>,
                  }}
                />

                <Button
                  type="submit" fullWidth variant="contained" size="large"
                  disabled={regLoading || regOtp.length !== 6}
                  sx={{ py: 1.4, fontWeight: 700, fontSize: 14, borderRadius: 2, mb: 2 }}
                >
                  {regLoading ? <CircularProgress size={20} color="inherit" /> : 'Create My Account'}
                </Button>

                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Button
                    startIcon={<ArrowBack />} size="small"
                    onClick={() => { setRegStep(0); setRegOtp(''); setRegError(''); setRegSuccess(''); }}
                    sx={{ color: 'text.secondary', textTransform: 'none', fontSize: 12 }}
                  >
                    Back
                  </Button>
                  <Button
                    size="small" onClick={handleResend} disabled={resendTimer > 0}
                    sx={{
                      color: resendTimer > 0 ? 'text.secondary' : 'primary.main',
                      textTransform: 'none', fontSize: 12,
                    }}
                  >
                    {resendTimer > 0 ? `Resend in ${resendTimer}s` : 'Resend Code'}
                  </Button>
                </Box>
              </form>
            )}
          </>
        )}

        <Divider sx={{ my: 2.5, borderColor: 'rgba(255,255,255,0.08)' }} />
        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', color: 'text.secondary' }}>
          {tab === 0
            ? <>No account?{' '}
                <span onClick={() => { setTab(1); resetForms(); }}
                  style={{ color: '#4fc3f7', cursor: 'pointer', fontWeight: 600 }}>
                  Create one free
                </span>
              </>
            : <>Already a member?{' '}
                <span onClick={() => { setTab(0); resetForms(); }}
                  style={{ color: '#4fc3f7', cursor: 'pointer', fontWeight: 600 }}>
                  Sign in
                </span>
              </>
          }
        </Typography>
      </DialogContent>
    </Dialog>
  );
}
